//#region [IMPORT]

import React, { createContext, useEffect, useState, useRef } from 'react';

// === STYLE / MATERIAL UI ===
import { useTheme } from '@mui/material/styles';
import { Box, CardHeader, CardContent, IconButton } from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';

// === COMPONENTS ===
import { Thumbnail } from './components/Thumbnail';
import { DocumentPlaceholder } from './DocumentPlaceholder';

// === API ===
import { isFocusedDocument, moveDocument, onDocument, onDocumentFocusId, removeListener, requestDocument } from 'api/prokey/prokey.websocket';

//#endregion

//-----------------------------------------------------------------
export function Document( props ) {
  //-----------------------------------------------------------------

  //#region [VALUES]

  const { id, timestamp, index, images, scroll_selected_id } = props;

  const theme = useTheme();
  const ref   = useRef();
  
  const test = isFocusedDocument( id );

  const [ is_open    , setIsOpen    ] = useState( test );
  const [ was_opened , setWasOpened ] = useState( test );
  const [ document   , setDocument  ] = useState({});

  const has_doc = !!document.Fields;

  const isVisible = true;

  const expand_document_icon = is_open ? <ExpandLess /> : <ExpandMore />;

  const [is_drop      , setIsDrop     ] = useState(false);
  const [is_drop_above, setIsDropAbove] = useState(false);

  let placeholder = <DocumentPlaceholder id={id} />;

  // let counter = 0;

  //#endregion

  //#region [STYLES]

  const _styles = {
    container: {
      display  : isVisible ? '' : 'none',
      variant  : 'primary',
      borderRadius: '0.5rem',
      width: '100%',
      margin: 0,
      marginBottom: '1rem',
      backgroundColor: theme.palette.mode === 'light' ? '' : theme.palette.background.primary[theme.palette.mode],
    },
    header: {
      boxShadow: '-0.5em 0  0.01em' + theme.palette.primary.main,
      padding: '1rem',
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      height: '4rem',
      zIndex: 5,
    },
    content: {
      display: is_open ? '' : 'none',
      margin: 0,
      padding: '1rem',
      width: '100%',
      overflow: 'auto',
    },
    blocks: {
      height: '100%',
      width: '100%',
      overflowY: 'auto',
    }
  }

  const CONTAINER = {
    sx  : _styles.container,
    ref : ref,
    id  : id,
    draggable   : true,
    onDragStart : onDragStart,
    onDragEnter : onDragEnter,
    onDragLeave : onDragLeave,
    onDragOver  : onDragOver ,
    onDragEnd   : onDragEnd  ,
    onDrop      : onDrop     ,
  }

  //#endregion

  //#region [EFFECT]

  useEffect( () => {
    //-----------------------------------------------------------------
    function handleDocument(data) {
      //-----------------------------------------------------------------
      setDocument(data)
    }
    //-----------------------------------------------------------------
    function handleDocumentFocus(data) {
      //-----------------------------------------------------------------
      try {
        let open = data?.open;
  
        setIsOpen( open )
        if(!was_opened && open) setWasOpened(true);
      } catch (error) {
        console.log( error );
      }
    }

    onDocument       ( id, handleDocument      );
    onDocumentFocusId( id, handleDocumentFocus );

    requestDocument( id );
    return () => {
      removeListener( 'DOCUMENT-'       + id, handleDocument      );
      removeListener( 'DOCUMENT-FOCUS-' + id, handleDocumentFocus );
    }
  }, [id, timestamp] );

  //#endregion

  //#region [EVENTS]

  //-----------------------------------------------------------------
  function handleClick() {
    //-----------------------------------------------------------------
    if(!was_opened) setWasOpened(true);
    setIsOpen(!is_open);
  }

  //-----------------------------------------------------------------
  function onDragStart( event ) {
    //-----------------------------------------------------------------
    event.stopPropagation();
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData(    'id/document',    id );
    event.dataTransfer.setData( 'index/document', index );
    event.target.style.opacity = 1.0;
    setIsOpen( false );
  }

  //-----------------------------------------------------------------
  function onDragEnd( event ) {
    //-----------------------------------------------------------------
    event.stopPropagation();
  }

  //-----------------------------------------------------------------
  function onDragEnter( event ) {
    //-----------------------------------------------------------------
    const active_id = event.dataTransfer.getData( "id/document" );
    if( active_id == id ) return;

    const is_document = event.dataTransfer.types.includes("id/document");
    if ( !is_document ) return;

    event.stopPropagation();
    event.preventDefault();
    
    if( !is_drop ) {
      setIsDrop(true);
    }
  }

  //-----------------------------------------------------------------
  function onDragLeave( event ) {
    //-----------------------------------------------------------------
    const active_id = event.dataTransfer.getData("id/document");
    if( active_id == id ) return;

    const is_document = event.dataTransfer.types.includes("id/document");
    if ( !is_document ) return;

    event.stopPropagation();
    event.preventDefault();

    const rect = ref.current.getBoundingClientRect();
    if(event.clientY < rect.top || event.clientY >= rect.bottom || event.clientX < rect.left || event.clientX >= rect.right) {
      setIsDrop(false);
    }
  }

  //-----------------------------------------------------------------
  function onDragOver( event ) {
    //-----------------------------------------------------------------
    const active_index = event.dataTransfer.getData( "index/document" );
    const active_id    = event.dataTransfer.getData(    "id/document" );
    if( active_id == id ) return;

    const is_document = event.dataTransfer.types.includes("id/document");
    if ( !is_document ) return;
    
    event.stopPropagation();
    event.preventDefault();

    const is_directly_above = index - active_index ===  1;
    const is_directly_below = index - active_index === -1;

    const bounding = ref.current.getBoundingClientRect()
    const offset = bounding.y + (bounding.height/2);
    const is_mouse_below = event.clientY - offset > 0;
    const is_mouse_above = event.clientY - offset < 0;

    if( is_directly_above && is_mouse_above ) setIsDrop( false );
    if( is_directly_below && is_mouse_below ) setIsDrop( false );

    if( is_mouse_below &&  is_drop_above ) return setIsDropAbove( false );
    if( is_mouse_above && !is_drop_above ) return setIsDropAbove(  true );
  }

  //-----------------------------------------------------------------
  function onDrop( event ) {
    //-----------------------------------------------------------------
    const is_document = event.dataTransfer.types.includes("id/document");
    if ( !is_document ) return;

    const active_id = event.dataTransfer.getData("id/document");
    if( active_id == id ) return;

    event.stopPropagation();
    event.preventDefault();

    setIsDrop(false);
    moveDocument( active_id, id, is_drop_above );
  }

  //#endregion

  //#region [DISPLAY]

  //-----------------------------------------------------------------
  function display() {
    //-----------------------------------------------------------------
    return has_doc ? (
      <Box { ...CONTAINER } key={id} >
        { is_drop &&  is_drop_above ? placeholder : null }
        { displayMain() }
        { is_drop && !is_drop_above ? placeholder : null }
      </Box>
    ) : (
      <Box ref={ref} key={id} sx={_styles.container} id={'thumb-doc-container-' + id } >
        <CardHeader id={'thumb-doc-header-' + id } sx={_styles.header} title='Loading...' />
      </Box>
    );
  }

  //-----------------------------------------------------------------
  function displayMain() {
    //-----------------------------------------------------------------
    return (
      <>
        <CardHeader id={'thumb-doc-header-' + id } sx={_styles.header} title={`${document.Type} - ${document.Name}`} onClick={handleClick}
          action={<IconButton onClick={handleClick}>{expand_document_icon}</IconButton>} />
        {displayThumbnails()}
      </>
    )
  }

  //-----------------------------------------------------------------
  function displayThumbnails() {
    //-----------------------------------------------------------------
    if(!was_opened) return null;

    const thumbs = document.Images.map( ( id_image ) => (
      <Box key={ id_image } data-thumb-id={ id_image } data-doc-id={ id }>
        <Thumbnail
          id={ id_image }
          index={ images.indexOf( id_image ) }
          id_doc={ id }
          scroll_selected_id={ scroll_selected_id }
        />
      </Box>
    ) );

    return (
      <Box id={'doc-thumb-' + id } sx={_styles.content}>
        {thumbs}
      </Box>
    );
  }

  //#endregion

  return display();
}